<template>
    <div :class="mode">
        <LMap
            ref="map"
            :options="{
                ...mapOptions,
                editable: true,
            }"
            :max-bounds="maxBounds"
            :max-bounds-viscosity="maxBoundsViscosity"
            :max-zoom="activeTileProvider.maxZoom"
            :min-zoom="activeTileProvider.minZoom"
            @update:zoom="zoom = $event"
            @locationfound="onLocationFound"
            @locationerror="onLocationError"
            @mouseup="setShouldFollowActiveTrackerUpdates(false)"
            @mousedown="stopUpdatingTrackers"
            @click="onMapClicked"
        >
            <LocationLayer
                v-if="mapDisplayPreferences.showLocations"
                :locations="locationsToDisplay"
                @alignMap="alignMap"
            />

            <HistoryLayer v-if="mode === 'history'" @alignMap="alignMap" />

            <TripHistoryLayer
                v-if="mode === 'triphistory'"
                :map-zoom="zoom"
                @alignMap="alignMap"
            />

            <HistoryPointLayer
                v-if="mode === 'history' || mode === 'triphistory'"
            />

            <ConnectionHeatmapLayer
                v-if="mode === 'connection-heatmap'"
                :measurements="heatmapData || []"
                @alignMap="alignMap"
            />

            <CreateLocationLayer v-if="mode === 'editing'" />

            <TrackerLayer
                v-if="isTrackerLayerVisible"
                :trackers="trackersToDisplay"
                :excluded-from-clustering="trackersExcludedFromClustering"
                @alignMap="alignMap"
                @markerClicked="onMarkerClicked"
            />

            <RadialSearchLayer
                v-if="isRadialSearchLayerVisible"
                @alignMap="alignMap"
            />

            <AccuracyCircle v-if="mapDisplayPreferences.showAccuracy" />

            <TrackerTraceLayer :coordinates="activeTrackerTrace" />

            <UserLayer />

            <LControlAttribution position="bottomright" :prefix="appVersion" />

            <LControlScale position="bottomleft" :imperial="false" />
        </LMap>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { debounce } from 'lodash'
import { Icon } from 'leaflet'
import { LControlAttribution, LControlScale, LMap } from 'vue2-leaflet'

Icon.Default.imagePath = `${location.pathname}/img/leaflet/`.replace(
    /\/{2}/g,
    '/'
)

import AccuracyCircle from './AccuracyCircle'
import AxRoadHelpers from '@/mixins/AxRoadHelpers'
import ConnectionHeatmapLayer from './ConnectionHeatmapLayer'
import CreateLocationLayer from './CreateLocationLayer'
import HistoryLayer from './HistoryLayer'
import HistoryPointLayer from './HistoryPointLayer'
import LocationLayer from './LocationLayer'
import MapHelper from '@/mixins/MapHelper'
import RadialSearchLayer from './RadialSearchLayer'
import TrackerLayer from './TrackerLayer'
import TrackerTraceLayer from './TrackerTraceLayer'
import TripHistoryLayer from './TripHistoryLayer'
import UserLayer from './UserLayer'

export default {
    name: 'AxMap',
    components: {
        AccuracyCircle,
        ConnectionHeatmapLayer,
        CreateLocationLayer,
        HistoryLayer,
        HistoryPointLayer,
        LControlAttribution,
        LControlScale,
        LMap,
        LocationLayer,
        TrackerLayer,
        TrackerTraceLayer,
        TripHistoryLayer,
        UserLayer,
        RadialSearchLayer,
    },
    mixins: [AxRoadHelpers, MapHelper],
    data() {
        return {
            appVersion: process.env.VUE_APP_VERSION,
            startUpdatingTrackers: () => {},
            zoom: null,
        }
    },
    computed: {
        ...mapState('axroad', ['radialSearchCenter']),
        ...mapState('location', ['activeLocationOnMap', 'locations']),
        ...mapState('map', ['heatmapData', 'savedBounds']),
        ...mapState('tracker', [
            'activeTrackerOnMap',
            'activeTrackerTrace',
            'shouldFollowActiveTrackerUpdates',
            'trackers',
        ]),
        ...mapGetters('auth', ['isAxRoad']),
        ...mapGetters('axroad', ['canPerformRadialSearch']),
        ...mapGetters('map', ['activeTileProvider']),
        ...mapGetters('tracker', ['trackersFilteredByType']),
        ...mapGetters('preferences', ['mapDisplayPreferences']),
        mapCenterOffset() {
            return Array.from(
                document.querySelectorAll('.ax-road-page')
            ).reduce((acc, cur) => {
                return acc + cur.clientWidth
            }, document.querySelector('.ax-road-side-bar')?.clientWidth ?? 0)
        },
        isTrackerLayerVisible() {
            switch (this.mode) {
                case 'connection-heatmap':
                case 'history':
                case 'triphistory':
                    return false
                default:
                    return true
            }
        },
        isRadialSearchLayerVisible() {
            return (
                this.isAxRoad &&
                this.mode === 'ax-road-search' &&
                this.canPerformRadialSearch
            )
        },
        trackersExcludedFromClustering() {
            return this.activeTrackerOnMap ? [this.activeTrackerOnMap] : []
        },
        trackersToDisplay() {
            return this.trackersFilteredByType.filter(
                tracker =>
                    tracker.asset_details.position.latitude &&
                    tracker.asset_details.position.longitude &&
                    (this.axRoadMode === 'drivers'
                        ? !!tracker.asset_details?.sensor_data?.driver_name
                              ?.value
                        : true)
            )
        },
        locationsToDisplay() {
            // do not show active location if editing a location
            if (this.mode == 'editing' && this.activeLocationOnMap) {
                return this.locations.filter(
                    location => location.id != this.activeLocationOnMap.id
                )
            }

            return this.locations
        },
        mode() {
            switch (this.$route.name) {
                case 'axRoadDriverDetails':
                case 'axRoadDrivers':
                    return 'ax-road-drivers'
                case 'axRoadSearch':
                case 'axRoadSearchResults':
                    return 'ax-road-search'
                case 'axRoadTruckDetails':
                case 'axRoadTrucks':
                    return 'ax-road-trucks'
                case 'assetConnectionHeatmap':
                case 'connectionHeatmap':
                case 'locationConnectionHeatmap':
                    return 'connection-heatmap'
                case 'axRoadAddZone':
                case 'axRoadEditZone':
                case 'createlocation':
                case 'editlocation':
                    return 'editing'
                case 'locationhistory':
                    return 'history'
                case 'triphistory':
                    return 'triphistory'
                default:
                    return 'default'
            }
        },
        zoomBoundary() {
            return this.isAxRoad ? 16 : 12
        },
    },
    watch: {
        activeTrackerTrace(trace) {
            if (!trace || trace.length === 0) {
                return
            }
            const center = this.isAxRoad
                ? this.offsetByPx(trace[0], [-this.mapCenterOffset / 2, 0])
                : trace[0]
            if (this.shouldFollowActiveTrackerUpdates && trace.length > 1) {
                this.$refs.map.mapObject.panTo(center, {
                    animate: true,
                    duration: 1,
                })
            }
        },
        radialSearchCenter(center) {
            if (this.isRadialSearchLayerVisible && center) {
                this.$refs.map.mapObject.panTo(
                    this.offsetByPx(center, [-this.mapCenterOffset / 2, 0]),
                    {
                        animate: true,
                        duration: 1,
                    }
                )
            }
        },
    },
    mounted() {
        this.setMapInstance(this.$refs.map.mapObject)

        this.startUpdatingTrackers = debounce(() => {
            this.setShouldUpdateTrackers(true)
        }, 700)

        const updatePauseEvents = ['zoomstart', 'move', 'zoom']
        const updateResumeEvents = ['moveend', 'zoomend']

        this.$refs.map.mapObject.on(
            updatePauseEvents.join(' '),
            this.stopUpdatingTrackers
        )
        this.$refs.map.mapObject.on(
            updateResumeEvents.join(' '),
            this.startUpdatingTrackers
        )

        if (this.savedBounds) {
            this.$refs.map.mapObject.fitBounds(this.savedBounds)
            this.clearSavedBounds()
        } else {
            const bounds = this.trackers
                .filter(item => item.asset_details)
                .map(item => [item.asset_details.lat, item.asset_details.lng])
            if (bounds.length) {
                this.$refs.map.mapObject.fitBounds(bounds)
            }
        }
    },
    beforeDestroy() {
        this.disablePositionTracking()
    },
    methods: {
        ...mapMutations('map', [
            'clearSavedBounds',
            'setMapInstance',
            'setPositionTrackingEnabled',
            'setUserPosition',
        ]),
        ...mapMutations('tracker', [
            'setShouldFollowActiveTrackerUpdates',
            'setShouldUpdateTrackers',
        ]),
        ...mapActions('axroad', ['fetchAddressForCoordinates']),
        areCoordinatesVisible(coordinates) {
            return coordinates.every(item =>
                this.$refs.map.mapObject.getBounds().contains(item)
            )
        },
        alignMap(bounds) {
            if (
                bounds?.length &&
                (this.$refs.map.mapObject.getZoom() < this.zoomBoundary ||
                    !this.areCoordinatesVisible(bounds) ||
                    this.mapDisplayPreferences.forceAlign)
            ) {
                this.$nextTick(
                    debounce(() => {
                        this.$refs.map.fitBounds(
                            bounds,
                            this.isAxRoad
                                ? {
                                      paddingTopLeft: [this.mapCenterOffset, 0],
                                  }
                                : {}
                        )
                    }, 100)
                )
            }
        },
        disablePositionTracking() {
            this.$refs.map.mapObject.stopLocate()
            this.setPositionTrackingEnabled(false)
        },
        onLocationFound({ latitude, longitude }) {
            this.setUserPosition({
                lat: latitude,
                lng: longitude,
            })
        },
        onLocationError() {
            this.disablePositionTracking()
        },
        stopUpdatingTrackers() {
            this.startUpdatingTrackers.cancel()
            this.setShouldUpdateTrackers(false)
        },
        onMarkerClicked($event) {
            if (this.isAxRoad) {
                this.$router.push({
                    name:
                        this.axRoadMode === 'drivers' && $event
                            ? 'axRoadDriverDetails'
                            : 'axRoadTruckDetails',
                    params: $event && {
                        id: $event,
                    },
                })
            } else {
                this.$router.push({
                    name: 'detail',
                    params: {
                        id: $event,
                    },
                })
            }
        },
        onMapClicked($event) {
            if (this.isAxRoad && this.mode === 'ax-road-search') {
                this.fetchAddressForCoordinates({
                    ...$event.latlng,
                    language: this.$i18n.locale,
                })
            }
        },
        offsetByPx(latLng, pixelOffset) {
            const point = this.$refs.map.mapObject.latLngToContainerPoint(
                latLng
            )
            const offsetPoint = point.add(pixelOffset)
            return this.$refs.map.mapObject.containerPointToLatLng(offsetPoint)
        },
    },
}
</script>

<style lang="scss">
.editing {
    path.leaflet-interactive {
        cursor: grab;
    }

    .leaflet-marker-draggable {
        width: 14px !important;
        height: 14px !important;
        margin-left: -7px !important;
        margin-top: -7px !important;
        border-radius: 2px;
        cursor: crosshair;
    }
}

.leaflet-popup-content-wrapper {
    border-radius: 4px;
}
</style>
